/**
 * tokenManager — singleton that holds the current fleet JWT and handles:
 *
 *  1. Proactive refresh: schedules a timer to fire ~60s before exp.
 *  2. On-focus / visibility refresh: calls ensureFresh() when the tab becomes
 *     visible again (catches laptop-sleep gaps where the timer never fired).
 *  3. On-demand refresh: called by the api client on a 401 response, with
 *     deduplication so concurrent requests never fire more than one POST.
 *
 * No React imports — this module is framework-agnostic so the api client can
 * use it without pulling in React.
 */

const TOKEN_SESSION_KEY = 'mc_token'

/** How many milliseconds before exp we proactively refresh. */
const PROACTIVE_LEAD_MS = 60_000

/** If the gap to expiry is less than this, ensureFresh() triggers a refresh. */
const ENSURE_FRESH_THRESHOLD_MS = 90_000

/** Minimum delay before the proactive refresh timer fires (avoids 0-ms timers). */
const MIN_TIMER_MS = 5_000

// ---------------------------------------------------------------------------
// JWT exp decoder — framework-agnostic, no dependencies
// ---------------------------------------------------------------------------

/**
 * decodeExp parses the JWT payload and returns the `exp` claim in seconds, or
 * null if the token is malformed or has no `exp`.
 */
export function decodeExp(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null

    // Convert base64url → base64 → binary string → JSON.
    const base64url = parts[1]
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
    // Pad to multiple of 4.
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const payload: unknown = JSON.parse(atob(padded))

    if (typeof payload !== 'object' || payload === null) return null
    const exp = (payload as Record<string, unknown>)['exp']
    if (typeof exp !== 'number') return null
    return exp
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Fleet-token mint
// ---------------------------------------------------------------------------

/**
 * mintFleetToken — exchanges the console session cookie for a fleet JWT.
 *
 * POSTs to the console /auth/fleet-token with credentials; the console echoes
 * this MC origin with Access-Control-Allow-Credentials (its FleetTokenCORS
 * middleware) and mints only if the session is valid AND the caller holds a
 * grant for `fleetSlug`. Returns null on any failure — no session, no grant, or
 * a network/CORS error.
 *
 * Two callers:
 *  1. TokenManager.refresh() — the silent-refresh path (ADR-023).
 *  2. App bootstrap — a COLD start with nothing in sessionStorage, which is what
 *     happens when an operator follows a deep link (a shared chat permalink, a
 *     Slack @mention link) into a fresh tab. Minting in place keeps them on the
 *     URL they asked for; bouncing them to the console would land them back on
 *     MC's root and silently discard the link.
 */
export async function mintFleetToken(
  consoleUrl: string,
  fleetSlug: string,
): Promise<string | null> {
  try {
    const resp = await fetch(`${consoleUrl}/auth/fleet-token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fleet: fleetSlug }),
    })
    if (!resp.ok) return null
    const body = (await resp.json()) as { token?: string; expires_in?: number }
    return body.token ?? null
  } catch {
    // Network / CORS error.
    return null
  }
}

// ---------------------------------------------------------------------------
// TokenManager
// ---------------------------------------------------------------------------

interface InitOptions {
  token: string
  consoleUrl: string
  fleetSlug: string
  /** Called when a refresh attempt fails (session truly gone). */
  onExpired: () => void
}

class TokenManager {
  private token = ''
  private exp: number | null = null
  private consoleUrl = ''
  private fleetSlug = ''
  private onExpired: (() => void) | null = null

  /** Timer id for the proactive refresh schedule. */
  private timerId: ReturnType<typeof setTimeout> | null = null

  /**
   * Single in-flight refresh promise — any concurrent caller awaits the same
   * Promise so only one POST is ever in-flight at a time.
   */
  private inFlight: Promise<boolean> | null = null

  // Bound event handlers so we can remove them in stop().
  private readonly handleVisibility = () => { void this.ensureFresh() }
  private readonly handleFocus = () => { void this.ensureFresh() }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * init — called once after a successful getMe(). Stores the token, wires up
   * the proactive timer, and attaches focus/visibility listeners.
   */
  init(opts: InitOptions): void {
    this.token = opts.token
    this.exp = decodeExp(opts.token)
    this.consoleUrl = opts.consoleUrl
    this.fleetSlug = opts.fleetSlug
    this.onExpired = opts.onExpired

    sessionStorage.setItem(TOKEN_SESSION_KEY, opts.token)
    this.scheduleRefresh()

    document.addEventListener('visibilitychange', this.handleVisibility)
    window.addEventListener('focus', this.handleFocus)
  }

  /** getToken — returns the current (possibly just-refreshed) token. */
  getToken(): string {
    return this.token
  }

  /**
   * ensureFresh — called on focus/visibility events. If the token is within
   * ENSURE_FRESH_THRESHOLD_MS of expiry (or already past), triggers a refresh.
   * No-ops if the token is still fresh.
   */
  async ensureFresh(): Promise<void> {
    if (!this.exp) return
    const msToExp = this.exp * 1000 - Date.now()
    if (msToExp <= ENSURE_FRESH_THRESHOLD_MS) {
      await this.refresh()
    }
  }

  /**
   * refresh — POSTs to the console /auth/fleet-token endpoint with the session
   * cookie (credentials:'include'). Deduplicates concurrent callers via a single
   * in-flight Promise.
   *
   * Returns true if the refresh succeeded (token updated), false if the session
   * is gone (onExpired has been called, sessionStorage cleared).
   */
  refresh(): Promise<boolean> {
    // Dedupe: if a refresh is already in-flight, return the same promise.
    if (this.inFlight) return this.inFlight

    this.inFlight = this._doRefresh().finally(() => {
      this.inFlight = null
    })
    return this.inFlight
  }

  /**
   * stop — clears the timer and removes event listeners. Call on unmount or
   * logout to prevent the manager from outliving the React tree.
   */
  stop(): void {
    this._clearTimer()
    document.removeEventListener('visibilitychange', this.handleVisibility)
    window.removeEventListener('focus', this.handleFocus)
    this.inFlight = null
    this.onExpired = null
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * scheduleRefresh — computes the delay until the proactive refresh should
   * fire (exp - 60s, but at least 5s) and sets a timer.
   */
  scheduleRefresh(): void {
    this._clearTimer()
    if (!this.exp) return

    const delay = Math.max(MIN_TIMER_MS, this.exp * 1000 - Date.now() - PROACTIVE_LEAD_MS)
    this.timerId = setTimeout(() => {
      void this.refresh()
    }, delay)
  }

  private _clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
  }

  private async _doRefresh(): Promise<boolean> {
    const token = await mintFleetToken(this.consoleUrl, this.fleetSlug)
    if (!token) {
      // No session, no grant, or a network/CORS error — all mean the console
      // session can no longer vouch for us.
      this._onSessionGone()
      return false
    }

    // Success — update state, persist, reschedule.
    this.token = token
    this.exp = decodeExp(token)
    sessionStorage.setItem(TOKEN_SESSION_KEY, token)
    this.scheduleRefresh()
    return true
  }

  private _onSessionGone(): void {
    sessionStorage.removeItem(TOKEN_SESSION_KEY)
    this._clearTimer()
    this.onExpired?.()
  }
}

/** Singleton instance — import this, never construct TokenManager directly. */
export const tokenManager = new TokenManager()
